import React, { useEffect, useState } from "react";
import { X, Sparkles, Database, GitBranch, MessageSquare } from "lucide-react";

const ONBOARDING_KEY = "pulse-ai-hub:onboarding:dismissed";

export function OnboardingTour() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(ONBOARDING_KEY);
    if (!dismissed) {
      setIsOpen(true);
    }
  }, []);

  const handleDismiss = () => {
    setIsOpen(false);
    localStorage.setItem(ONBOARDING_KEY, "true");
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed bottom-20 left-4 right-4 z-30 mx-auto max-w-md animate-in fade-in duration-200 sm:bottom-24"
      role="dialog"
      aria-labelledby="onboarding-tour-title"
      aria-describedby="onboarding-tour-desc"
    >
      <div className="relative w-full rounded-2xl border border-slate-700 bg-slate-800 p-6 shadow-2xl overflow-hidden">
        {/* Glow effect */}
        <div className="absolute -top-24 -right-24 h-48 w-48 rounded-full bg-blue-500/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 h-48 w-48 rounded-full bg-purple-500/20 blur-3xl pointer-events-none" />

        <div className="relative z-10">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/20">
                <Sparkles size={20} className="text-white" />
              </div>
              <div>
                <h2 id="onboarding-tour-title" className="text-lg font-bold text-white">Welcome to Pulse AI Hub 2.0</h2>
                <p id="onboarding-tour-desc" className="text-sm text-slate-400">Here's what's new in this release</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
              aria-label="Dismiss tour"
            >
              <X size={18} />
            </button>
          </div>

          <div className="space-y-4 mb-8">
            <FeatureItem
              icon={<Database size={18} className="text-emerald-400" />}
              title="Cost Analytics"
              description="Track AI usage and costs across your entire fleet in real-time."
            />
            <FeatureItem
              icon={<MessageSquare size={18} className="text-blue-400" />}
              title="Chat Persistence"
              description="Your conversations are now automatically saved and sync across devices."
            />
            <FeatureItem
              icon={<GitBranch size={18} className="text-purple-400" />}
              title="Workflow Builder"
              description="Design complex multi-agent workflows with our new visual editor."
            />
          </div>

          <button
            onClick={handleDismiss}
            className="w-full rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-800 shadow-lg shadow-blue-600/20"
          >
            Get Started
          </button>
        </div>
      </div>
    </div>
  );
}

function FeatureItem({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-slate-700/50 bg-slate-900/50 p-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-800 border border-slate-700">
        {icon}
      </div>
      <div>
        <h3 className="text-sm font-semibold text-slate-200">{title}</h3>
        <p className="text-xs text-slate-400 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}
